import { useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { cancelRun, runQuery, runTargetsQuery, targetDrawerQuery, type TargetRow } from '../../api/admRuns'
import { shellQuery } from '../../api/shell'
import { useScanLive } from '../../api/admScanLive'
import { Content } from '../../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import {
  OUTCOME_TONE,
  RUN_TONE,
  STATE_PILL,
  TARGET_HEADS,
  TargetDrawerBody,
  targetCellClass,
  targetCellHtml,
} from './AdmRunBits'

/**
 * ADM -> o singura scanare, din `views/version-3/softsee/adm/runs/detail.php`.
 *
 * IMPARTIREA PE SFARSITURI E ROSTUL ECRANULUI. "Patruzeci de masini au
 * raspuns" nu e un raspuns; "patruzeci au raspuns, douasprezece n-au putut fi
 * clasificate, trei n-au avut acreditare" ii spune cuiva ce sa repare si pe
 * cine sa intrebe. De-aia se si poate apasa pe fiecare rand al ei: filtreaza
 * lista de dedesubt.
 *
 * COLOANELE SUNT ALE SERVERULUI, nu ale fisierului asta. Ce a inregistrat
 * chiar scanarea hotaraste ce coloane exista, iar capetele, celulele si
 * sortarea se fac din ACELASI sir - o scanare fara producator n-are coloana de
 * producator, si atunci n-are cum sa se sorteze dupa ea.
 */

const STYLES = ['css/adm.css']

/** Sertarul unei adrese: se cere abia cand randul chiar se deschide. */
function TargetDrawer({ id }: { id: number }) {
  const drawer = useQuery(targetDrawerQuery(id))

  if (drawer.isPending) {
    return (
      <div className="adm-expand adm-muted">
        <i className="fas fa-circle-notch fa-spin"></i> Loading…
      </div>
    )
  }

  if (drawer.isError || !drawer.data) {
    return <div className="adm-expand adm-muted">This address could not be loaded.</div>
  }

  return <TargetDrawerBody target={drawer.data.target} rows={drawer.data.rows} total={drawer.data.total} />
}

/** Cheile dupa care lista de adrese chiar poate fi ordonata - `TARGET_ORDER`. */
const SORTABLE = ['ip', 'hostname', 'mac', 'manufacturer', 'response', 'outcome', 'updated']

export function RunDetailPage({ id }: { id: number }) {
  const shellData = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shellData.data?.version ?? '')

  const [outcome, setOutcome] = useState('')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [confirmCancel, setConfirmCancel] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const page = useQuery(runQuery(id))
  const keys = useMemo(() => page.data?.targetColumns ?? [], [page.data])

  const [state, setState] = useTableState('admTargets', {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'asc' },
    search: '',
  })

  /* Ce cheie s-a cerut: indicele coloanei se traduce prin LISTA serverului, nu
     se socoteste a doua oara aici. */
  const orderKey = keys[state.order.column] ?? 'ip'

  const targets = useQuery({
    ...runTargetsQuery(id, {
      draw: 1,
      start: state.start,
      length: state.length,
      orderKey: SORTABLE.includes(orderKey) ? orderKey : 'ip',
      orderDir: state.order.dir,
      search: debounced,
      outcome,
    }),
    placeholderData: keepPreviousData,
    enabled: keys.length > 0,
  })

  /* Cadrele scanarii ASTEIA (bilet cu `run:<id>`): fisa si tintele se
     recitesc pe fiecare cadru - `SCAN_PROGRESS` / `SCAN_TARGETS` in adm.js. */
  const live = useScanLive(id, true)
  useEffect(() => {
    if (live.frame > 0) {
      void page.refetch()
      void targets.refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.frame])

  const columns: TableColumn[] = useMemo(
    () =>
      keys.map((key, index) => ({
        index,
        title: TARGET_HEADS[key]?.label ?? key,
        orderable: SORTABLE.includes(key),
        visible: true,
        width: TARGET_HEADS[key]?.width,
        className: targetCellClass(key),
        html: (row: Record<string, unknown>) => targetCellHtml(key, row as unknown as TargetRow),
      })),
    [keys]
  )

  const run = page.data?.run

  const stop = async () => {
    try {
      const answer = await cancelRun(id)
      toast(answer.message, 'Discovery run', answer.status === 'OK' ? 'success' : 'error')
      setConfirmCancel(false)
      void page.refetch()
    } catch (error) {
      toast((error as Error).message, 'Discovery run', 'error')
    }
  }

  if (page.isError) {
    return (
      <Content>
        <div id="content">
          <div className="adm-empty-inline">
            <i className="fas fa-play-circle"></i> This run does not exist, or it is not this customer&apos;s.
          </div>
        </div>
      </Content>
    )
  }

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone={run ? (RUN_TONE[run.state] ?? 'grey') : 'grey'}
          icon="fa-play-circle"
          title={`Run #${id}`}
          note="one scan: what it covered, what answered, and what it could not read"
          stats={
            run
              ? [
                  { value: run.targetsResponded.toLocaleString('en-US'), label: 'devices seen', tone: 'grey' },
                  { value: run.ciCreated.toLocaleString('en-US'), label: 'new', tone: run.ciCreated > 0 ? 'green-mode' : 'grey' },
                  { value: run.ciUpdated.toLocaleString('en-US'), label: 'already known', tone: 'grey' },
                  { value: run.errorsCount.toLocaleString('en-US'), label: 'failures', tone: run.errorsCount > 0 ? 'red-intense' : 'grey' },
                ]
              : []
          }
        />

        <div className="adm-bar">
          <div className="adm-field adm-field-wide">
            <label>Customer</label>
            <div>{run?.companyName ?? ''}</div>
          </div>
          <div className="adm-field">
            <label>Type</label>
            <div>{run?.typeLabel ?? ''}</div>
          </div>
          <div className="adm-field">
            <label>Started by</label>
            <div>
              {run?.triggerLabel ?? ''}
              {run?.startedByName ? ` — ${run.startedByName}` : ''}
            </div>
          </div>
          <div className="adm-field">
            <label>Relay</label>
            <div>{run?.relayName || '—'}</div>
          </div>
          <div className="adm-field">
            <label>Scope</label>
            <div>{run?.rangeName || 'All ranges'}</div>
          </div>
          <div className="adm-field">
            <label>Duration</label>
            <div>{run?.duration ?? ''}</div>
          </div>

          <span className="adm-spacer"></span>

          <div className="adm-field">
            <label>State</label>
            <div>
              <span className={`adm-pill ${run ? (STATE_PILL[run.state] ?? '') : ''}`} id="admRunState">
                {run?.stateLabel ?? ''}
              </span>
            </div>
          </div>

          {run && run.canCancel && page.data?.canWrite ? (
            <button type="button" className="btn btn-danger btn-sm admCancelRun" onClick={() => setConfirmCancel(true)}>
              Cancel run
            </button>
          ) : null}

          <a className="btn btn-default btn-sm" href="/app/adm/runs">
            Back
          </a>
        </div>

        <div className="row">
          <div className="col-md-5">
            <div className="adm-card">
              <div className="adm-card-head">
                <h5>Progress</h5>
                <span className="adm-card-note">
                  {run?.startedAt ?? ''}
                  {run?.finishedAt ? ` → ${run.finishedAt}` : ''}
                </span>
              </div>

              {run && run.targetsTotal > 0 ? (
                <div id="admRunProgress">
                  <div className="adm-progress" style={{ height: 22 }}>
                    <span style={{ width: `${run.progress}%` }}></span>
                    <em style={{ lineHeight: '22px' }}>
                      {run.targetsResponded} of {run.targetsTotal} responded ({run.progress}%)
                    </em>
                  </div>
                </div>
              ) : (
                <div id="admRunNoTargets" className="adm-muted">
                  No targets recorded for this run yet.
                </div>
              )}

              <table className="table adm-list" style={{ marginTop: 14 }}>
                <tbody>
                  <tr>
                    <td>New devices (first seen in this run)</td>
                    <td className="adm-numeric" id="admRunCreated">
                      {run?.ciCreated ?? 0}
                    </td>
                  </tr>
                  <tr>
                    <td>Devices already known (seen again)</td>
                    <td className="adm-numeric">{run?.ciUpdated ?? 0}</td>
                  </tr>
                  <tr>
                    <td>Relations created</td>
                    <td className="adm-numeric">{run?.relationsCreated ?? 0}</td>
                  </tr>
                  <tr>
                    <td>Failures</td>
                    <td className="adm-numeric" id="admRunErrors">
                      {run && run.errorsCount > 0 ? <a href={`/app/adm/errors?company_id=${run.companyId}`}>{run.errorsCount}</a> : 0}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="col-md-7">
            <div className="adm-card">
              <div className="adm-card-head">
                <h5>What happened to each address</h5>
                <span className="adm-card-note">click a row to filter the list below</span>
              </div>

              {(page.data?.breakdown ?? []).length === 0 ? (
                <div className="adm-empty-inline">
                  <i className="fas fa-list"></i> Nothing has been recorded for this run yet.
                </div>
              ) : (
                <table className="table adm-list">
                  <tbody>
                    {(page.data?.breakdown ?? []).map((item) => (
                      <tr
                        key={item.outcome}
                        className={outcome === String(item.outcome) ? 'admOutcomeRow active' : 'admOutcomeRow'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setOutcome(String(item.outcome))
                          setState({ start: 0 })
                        }}
                      >
                        <td>
                          <span className={`adm-pill ${OUTCOME_TONE[item.outcome] ?? ''}`}>{item.title}</span>
                        </td>
                        <td className="adm-numeric">{item.value.toLocaleString('en-US')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <div className="adm-section">
          <h4>Targets</h4>
          <span className="adm-section-note" id="admOutcomeFilterNote">
            {outcome === ''
              ? ''
              : `showing "${(page.data?.breakdown ?? []).find((b) => String(b.outcome) === outcome)?.title ?? ''}"`}
          </span>
          {outcome === '' ? null : (
            <a
              className="adm-section-link"
              href="#"
              id="admClearOutcome"
              onClick={(event) => {
                event.preventDefault()
                setOutcome('')
                setState({ start: 0 })
              }}
            >
              Show all
            </a>
          )}
        </div>

        <div id="admTargetsTable">
          <DataTable
            id="admTargets"
            columns={columns}
            rows={(targets.data?.data ?? []) as unknown as Record<string, unknown>[]}
            total={targets.data?.recordsTotal ?? 0}
            fetching={targets.isFetching}
            error={targets.isError ? (targets.error as Error).message : null}
            onRetry={() => void targets.refetch()}
            state={state}
            onState={setState}
            rowId={(r) => String(r.DT_RowId ?? r.ID)}
            details={(row) => <TargetDrawer id={Number(row.ID)} />}
            footerButtons={{ copy: true, colvis: true, refresh: () => void targets.refetch() }}
            emptyHint="No address matches that."
            headerView={
              <input
                type="text"
                className="form-control input-sm"
                id="targetSearch"
                placeholder="address, name, MAC"
                autoComplete="off"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            }
          />
        </div>
      </div>

      <Modal
        id="admCancelRunModal"
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        title="Cancel this run"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setConfirmCancel(false)}>
              Leave it running
            </button>
            <button type="button" className="btn btn-danger" id="admCancelRunConfirm" onClick={() => void stop()}>
              Cancel the run
            </button>
          </>
        }
      >
        <p>Run #{id} stops where it is.</p>
        <p className="adm-muted">
          What it has already found stays — the addresses it has not reached yet simply will not be scanned. The next scheduled
          scan covers them again.
        </p>
      </Modal>
    </Content>
  )
}
